import { useState, useRef } from 'react';
import { useAuthStore } from '../stores/auth-store.js';

const API_BASE = import.meta.env.PROD
  ? 'https://cardealeros-api.bowling-anthony.workers.dev/v1'
  : '/v1';

interface PhotoCaptureProps {
  vehicleId: string;
  vehicleTitle: string;
  onDone: (count: number, mileage: number, notes: string) => void;
}

interface CapturedPhoto {
  id: string;
  previewUrl: string;
  status: 'uploading' | 'done' | 'error' | 'deleting';
}

export default function PhotoCapture({ vehicleId, vehicleTitle, onDone }: PhotoCaptureProps) {
  const [photos, setPhotos] = useState<CapturedPhoto[]>([]);
  const [mileage, setMileage] = useState('');
  const [notes, setNotes] = useState('');
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const capturePhoto = async (file: File, position: number) => {
    const tempId = `tmp_${Date.now()}_${position}`;
    const previewUrl = URL.createObjectURL(file);

    setPhotos((prev) => [...prev, { id: tempId, previewUrl, status: 'uploading' }]);

    try {
      const token = useAuthStore.getState().token;
      const res = await fetch(`${API_BASE}/vehicles/${vehicleId}/photos`, {
        method: 'POST',
        body: file,
        headers: {
          'Content-Type': file.type || 'image/jpeg',
          'X-Photo-Position': String(position),
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) throw new Error(`Upload failed: ${res.status}`);

      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'Upload failed');

      setPhotos((prev) =>
        prev.map((p) => (p.id === tempId ? { ...p, id: json.data.photo_id, status: 'done' } : p))
      );
    } catch {
      setPhotos((prev) =>
        prev.map((p) => (p.id === tempId ? { ...p, status: 'error' } : p))
      );
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const basePosition = photos.length;
    for (let i = 0; i < files.length; i++) {
      await capturePhoto(files[i], basePosition + i);
    }
    e.target.value = '';
  };

  const removePhoto = async (photoId: string) => {
    const photo = photos.find(p => p.id === photoId);
    if (!photo) return;

    // If it's a temp/error photo that never uploaded, just remove from state
    if (photo.id.startsWith('tmp_') || photo.status === 'error') {
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      URL.revokeObjectURL(photo.previewUrl);
      return;
    }

    // Delete from server
    setPhotos(prev => prev.map(p => p.id === photoId ? { ...p, status: 'deleting' } : p));
    try {
      const token = useAuthStore.getState().token;
      await fetch(`${API_BASE}/photos/${photoId}`, {
        method: 'DELETE',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
    } catch { /* best effort */ }

    setPhotos(prev => prev.filter(p => p.id !== photoId));
    URL.revokeObjectURL(photo.previewUrl);
  };

  const openCamera = () => cameraInputRef.current?.click();
  const openGallery = () => galleryInputRef.current?.click();

  const doneCount = photos.filter((p) => p.status === 'done').length;
  const errorCount = photos.filter((p) => p.status === 'error').length;
  const uploadingCount = photos.filter((p) => p.status === 'uploading').length;
  const deletingCount = photos.filter((p) => p.status === 'deleting').length;

  const miles = parseInt(mileage.replace(/\D/g, ''), 10) || 0;
  const canSubmit = uploadingCount === 0 && deletingCount === 0 && miles > 0;

  const handleMileageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value.replace(/\D/g, '').slice(0, 7);
    setMileage(raw ? new Intl.NumberFormat('en-US').format(parseInt(raw, 10)) : '');
  };

  return (
    <div className="space-y-6">
      {/* Photo Counter */}
      <div className="text-center">
        <p className="text-4xl font-bold text-brand-400">{doneCount}</p>
        <p className="text-surface-500 text-sm mt-1">
          {uploadingCount > 0
            ? `Uploading ${uploadingCount}...`
            : errorCount > 0
              ? `${doneCount} uploaded, ${errorCount} failed`
              : doneCount === 1
                ? 'photo uploaded'
                : 'photos uploaded'}
        </p>
      </div>

      {/* Photo Grid */}
      {photos.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {photos.map((p) => (
            <div key={p.id} className="relative aspect-square rounded-xl overflow-hidden">
              <img src={p.previewUrl} alt="" className="w-full h-full object-cover" />
              {p.status === 'uploading' && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {p.status === 'deleting' && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-red-400 border-t-transparent rounded-full animate-spin" />
                </div>
              )}
              {p.status === 'error' && (
                <div className="absolute inset-0 bg-red-900/50 flex items-center justify-center">
                  <span className="text-red-300 text-xs">Failed</span>
                </div>
              )}
              {/* Delete button — always visible except while uploading */}
              {p.status !== 'uploading' && p.status !== 'deleting' && (
                <button
                  onClick={() => removePhoto(p.id)}
                  className="absolute top-1 left-1 w-7 h-7 bg-black/70 hover:bg-red-600 rounded-full flex items-center justify-center transition-colors"
                >
                  <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round">
                    <line x1="3" y1="3" x2="11" y2="11" /><line x1="11" y1="3" x2="3" y2="11" />
                  </svg>
                </button>
              )}
              {p.status === 'done' && (
                <div className="absolute top-1 right-1 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-xs">ok</span>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Hidden inputs — camera vs gallery */}
      <input ref={cameraInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
      <input ref={galleryInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />

      {/* Camera / Gallery Buttons */}
      <div className="grid grid-cols-2 gap-3">
        <button onClick={openCamera} className="btn-primary py-4 text-sm font-semibold">
          Take Photo
        </button>
        <button onClick={openGallery} className="btn-secondary py-4 text-sm font-semibold">
          From Gallery
        </button>
      </div>

      {/* Mileage Input — inline */}
      <div>
        <label className="text-surface-400 text-xs block mb-1">Odometer</label>
        <input
          type="text"
          inputMode="numeric"
          placeholder="Enter mileage"
          className="input w-full text-lg"
          value={mileage}
          onChange={handleMileageChange}
        />
      </div>

      {/* Optional Notes */}
      <div>
        <label className="text-surface-400 text-xs block mb-1">Anything wrong or special? (optional)</label>
        <textarea
          placeholder="Dent on rear bumper, aftermarket exhaust, salvage title..."
          className="input w-full text-sm resize-none"
          rows={2}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>

      {/* Done Button */}
      <button
        onClick={() => onDone(doneCount, miles, notes)}
        disabled={!canSubmit}
        className="w-full bg-green-600 hover:bg-green-700 text-white font-bold rounded-xl py-4 text-lg transition-colors disabled:opacity-30"
      >
        {uploadingCount > 0 ? 'Uploading...' : !miles ? 'Enter mileage to continue' : `List ${vehicleTitle || 'Vehicle'}`}
      </button>

      {/* Skip photos */}
      {photos.length === 0 && miles > 0 && (
        <button onClick={() => onDone(0, miles, notes)} className="btn-ghost w-full text-sm">
          Skip photos for now
        </button>
      )}
    </div>
  );
}
